import { Store, select } from '@ngrx/store';
import {
  CanActivate,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { IStore } from 'src/app/core/root.store';
import { Observable, of } from 'rxjs';
import { mergeMap, map, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import * as fromAuth from '../store/auth.selectors';
import * as fromApproverAuth from '../store/approver/approver-auth.selectors';
import * as fromSearchAuth from '../store/searcher/searcher-auth.selectors';
import { getStrataPlanNumberFriendlyName } from 'src/app/core/store/ui/ui.selectors';
import { LocationStrategy } from '@angular/common';
import { UserLogin } from 'src/app/shared/services/roles.service';

@Injectable()
export class AuthGuard implements CanActivate {
  private isAdminPath: true;

  constructor(
    private store$: Store<IStore>,
    private router: Router,
    private locationStrategy: LocationStrategy
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    // fix for when user reloads page and is authenticated
    this.checkPathAndReload();
    return this.checkStoreAuthentication().pipe(
      mergeMap((storeAuth) => {
        if (
          storeAuth &&
          storeAuth.loggedIn &&
          this.checkTokenExpiry(storeAuth.expiresIn)
        ) {
          return of(true);
        }
        return of(false);
      }),
      map((authenticated) => {
        if (!authenticated) {
          this.store$
            .select(getStrataPlanNumberFriendlyName)
            .pipe(
              tap((friendlyName) => {
                if (friendlyName) {
                  let urlToNavigate;
                  if (
                    this.router.url.indexOf('approver') >= 0 ||
                    localStorage.getItem(environment.auth.user_login) ===
                      UserLogin.Approver
                  ) {
                    urlToNavigate = environment.auth.approver_login_url.replace(
                      'id',
                      friendlyName
                    );
                  } else if (
                    this.router.url.indexOf('searcher') >= 0 ||
                    localStorage.getItem(environment.auth.user_login) ===
                      UserLogin.Searcher
                  ) {
                    urlToNavigate = environment.auth.searcher_login_url.replace(
                      'id',
                      friendlyName
                    );
                  } else {
                    urlToNavigate = environment.auth.login_url.replace(
                      'id',
                      friendlyName
                    );
                  }
                  this.router.navigate([urlToNavigate]);
                }
              }),
              take(1)
            )
            .subscribe();
          return false;
        }
        return true;
      })
    );
  }

  checkStoreAuthentication() {
    if (
      this.router.url.indexOf(UserLogin.Approver) >= 0 ||
      localStorage.getItem(environment.auth.user_login) === UserLogin.Approver
    ) {
      return this.store$
        .pipe(
          select(
            fromApproverAuth.selectDefaultApproverAuthEntity(
              '5334f7c7-16e6-4714-9fce-8a2a6e60e79c'
            )
          )
        )
        .pipe(take(1));
    } else if (
      this.router.url.indexOf(UserLogin.Searcher) >= 0 ||
      localStorage.getItem(environment.auth.user_login) === UserLogin.Searcher
    ) {
      return this.store$
        .pipe(
          select(
            fromSearchAuth.selectDefaultSearcherAuthEntity(
              '5334f7c7-16e6-4714-9fce-8a2a6e60e79c'
            )
          )
        )
        .pipe(take(1));
    } else {
      return this.store$
        .pipe(
          select(
            fromAuth.selectDefaultAuthEntity(
              '5334f7c7-16e6-4714-9fce-8a2a6e60e79c'
            )
          )
        )
        .pipe(take(1));
    }
  }

  checkTokenExpiry(expiresIn: number): boolean {
    const currentUtcUnixTime = moment().utc().unix();
    return expiresIn > currentUtcUnixTime;
  }

  private checkPathAndReload() {
    let path = this.locationStrategy.path().split('/');
    if (
      path.length > 1 &&
      (path[1] === UserLogin.Admin ||
        path[1] === UserLogin.Approver ||
        path[1] === UserLogin.Searcher)
    ) {
      window.location.href = '/';
    }
    return;
  }
}
